import React, { useState, useEffect } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Library, Globe, Trophy, User, Sparkles, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBooks } from '../context/BookContext';
import { supabase } from '../lib/supabase';
import NewsCenterPanel from './NewsCenterPanel';
import ReadingVine from './ReadingVine';
import logo from '../assets/logo.jpg';
import InstallGuide from './InstallGuide';

const Layout = () => {
  const { user, profile } = useAuth();
  const { currentStreakCount } = useBooks();
  const [isNewsOpen, setIsNewsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const handleToast = (e) => {
      const { message, type = 'info' } = e.detail;
      const id = Date.now();
      setToasts(prev => [...prev, { id, message, type }]);
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, 4000);
    };

    window.addEventListener('show-toast', handleToast);
    return () => window.removeEventListener('show-toast', handleToast);
  }, []);

  const fetchNotifications = async () => {
    if (!user) return;
    
    // 1. Fetch notifications
    const { data: notificationsData, error: nError } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (nError) {
      console.error('Error fetching notifications:', nError);
      return;
    }
    
    // 2. Fetch read statuses for the current user
    const { data: readData, error: rError } = await supabase
      .from('user_notification_reads')
      .select('notification_id')
      .eq('user_id', user.id);
      
    if (rError) {
      console.error('Error fetching read status:', rError);
      return;
    }
    
    const readSet = new Set((readData || []).map(r => r.notification_id));
    
    // Map notifications to include is_read status
    const mapped = (notificationsData || []).map(n => ({
      ...n,
      is_read: readSet.has(n.id)
    }));
    
    const prefs = profile?.notification_prefs || { personal: true, group: true, community: true };
    const filtered = mapped.filter(n => prefs[n.category] !== false);
    setNotifications(filtered);
  };

  const handleMarkAsRead = async (id) => {
    const { error } = await supabase.rpc('mark_notification_as_read', { target_notification_id: id });
    if (error) {
      console.error('Error marking notification as read:', error);
    } else {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    }
  };

  const handleMarkAllAsRead = async () => {
    const { error } = await supabase.rpc('mark_all_notifications_as_read');
    if (error) {
      console.error('Error marking all notifications as read:', error);
    } else {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    }
  };

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }

    let userGroups = new Set();
    const fetchUserGroupsAndNotifications = async () => {
      // Fetch user groups to filter real-time group notifications
      const { data } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id);
      if (data) {
        userGroups = new Set(data.map(g => g.group_id));
      }
      await fetchNotifications();
    };

    fetchUserGroupsAndNotifications();

    const subscription = supabase
      .channel('public:notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications'
      }, async (payload) => {
        const newNotif = payload.new;
        
        const isPersonal = newNotif.user_id === user.id;
        const isGroupMember = newNotif.group_id && userGroups.has(newNotif.group_id);
        const isCommunity = newNotif.category === 'community' && !newNotif.user_id && !newNotif.group_id;
        
        const prefs = profile?.notification_prefs || { personal: true, group: true, community: true };
        if ((isPersonal || isGroupMember || isCommunity) && prefs[newNotif.category] !== false) {
          setNotifications(prev => [
            { ...newNotif, is_read: false },
            ...prev
          ]);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [user, profile]);

  const totalUnreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="app-container" style={{ 
      fontFamily: 'system-ui, sans-serif',
      backgroundColor: 'var(--bg-app)',
      color: 'var(--text-main)',
      minHeight: '100vh'
    }}>
      <header style={{ 
        padding: 'calc(1rem + var(--sat)) 1rem 1rem 1rem', 
        borderBottom: '1px solid var(--border)', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: 'var(--bg-header)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <ReadingVine streak={currentStreakCount} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src={logo} alt="Logo" style={{ height: '42px', borderRadius: '8px' }} />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-main)' }}>Leaf & Letter</h1>
            <div style={{ 
              display: 'inline-block', 
              fontSize: '0.6rem', 
              fontWeight: 'bold', 
              letterSpacing: '0.05rem',
              color: window.location.hostname.includes('test.') ? '#2196f3' : 'var(--text-soft)',
              backgroundColor: window.location.hostname.includes('test.') ? '#e3f2fd' : 'var(--bg-app)',
              padding: '1px 6px',
              borderRadius: '4px',
              border: '1px solid ' + (window.location.hostname.includes('test.') ? '#bbdefb' : 'var(--border)'),
              marginTop: '2px'
            }}>
              {window.location.hostname.includes('test.') ? 'TEST (BETA)' : 'STABLE'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          {user && (
            <button 
              onClick={() => setIsNewsOpen(true)}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                color: 'var(--text-soft)', 
                padding: '4px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Site News & Notifications"
            >
              <Bell size={20} />
              {totalUnreadCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  backgroundColor: '#ff9800',
                  color: 'white',
                  fontSize: '0.62rem',
                  fontWeight: 'bold',
                  borderRadius: '10px',
                  padding: '2px 5px',
                  minWidth: '12px',
                  textAlign: 'center',
                  lineHeight: 1,
                  boxShadow: '0 0 0 2px var(--bg-header)'
                }}>
                  {totalUnreadCount}
                </span>
              )}
            </button>
          )}
          
          {user ? (
            <Link to="/profile" style={{ color: 'var(--text-soft)', display: 'flex', alignItems: 'center' }}>
              <User size={20} />
            </Link>
          ) : (
            <Link to="/auth" style={{ color: 'var(--text-soft)', display: 'flex', alignItems: 'center' }}>
              <User size={20} />
            </Link>
          )}
        </div>
      </header>

      <main style={{ padding: '1rem', paddingBottom: 'calc(5rem + var(--sab))' }}>
        <Outlet />
      </main>

      <NewsCenterPanel 
        isOpen={isNewsOpen}
        onClose={() => setIsNewsOpen(false)}
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
      />

      <nav style={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        backgroundColor: 'var(--bg-nav)', 
        borderTop: '1px solid var(--border)', 
        display: 'flex', 
        justifyContent: 'space-around', 
        padding: '0.75rem 0 calc(0.75rem + var(--sab)) 0',
        zIndex: 100
      }}>
        <Link to="/community" style={navItemStyle}><Globe size={24} /> <span style={labelStyle}>Community</span></Link>
        <Link to="/me" style={navItemStyle}><User size={24} /> <span style={labelStyle}>Me</span></Link>
        <Link to="/library" style={navItemStyle}><Library size={24} /> <span style={labelStyle}>Library</span></Link>
        <Link to="/focus" style={navItemStyle}><Sparkles size={24} color="var(--accent)" /> <span style={labelStyle}>Focus</span></Link>
        <Link to="/bracket" style={navItemStyle}><Trophy size={24} /> <span style={labelStyle}>Garden</span></Link>
      </nav>

      <InstallGuide />

      {/* Global Toast Container */}
      <div style={toastContainerStyle}>
        {toasts.map(t => (
          <div key={t.id} style={{ ...toastCardStyle, borderLeft: `5px solid ${t.type === 'success' ? '#4caf50' : t.type === 'error' ? '#ff5252' : 'var(--accent)'}` }}>
            <span style={{ fontSize: '1.2rem' }}>
              {t.type === 'success' ? '🎉' : t.type === 'error' ? '❌' : 'ℹ️'}
            </span>
            <div style={{ flex: 1, fontSize: '0.85rem', fontWeight: '500', color: 'var(--text-main)' }}>{t.message}</div>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const navItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textDecoration: 'none',
  color: 'var(--text-soft)',
  fontSize: '0.7rem'
};

const labelStyle = {
  marginTop: '4px'
};

const toastContainerStyle = {
  position: 'fixed',
  top: '20px',
  right: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  zIndex: 9999,
  maxWidth: '320px',
  width: 'calc(100% - 40px)'
};

const toastCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(10px)',
  border: '1px solid var(--border)',
  borderRadius: '16px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
  animation: 'slideIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
};

export default Layout;